import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Menu, Modal } from '@ofself/shell';
import { Calendar, ChevronDown, UsersRound, X } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import NucleusPage, { FindField, Verb } from '../../components/nucleus/NucleusPage';
import Segmented from '../../components/nucleus/Segmented';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import { useOpen } from '../../components/nucleus/Inspector';
import StatusPill from '../../components/nucleus/StatusPill';
import Spark from '../../components/nucleus/Spark';
import DataTable from '../../components/nucleus/DataTable';
import { SetHead } from '../../components/nucleus/SetHead';
import { usePlace } from '../../nucleus/place';
import EntryInspector, { Face } from './EntryInspector';
import { KINDS, KIND_IDS, kindOf, viewsFor, GROUP_DEFAULT_KINDS, whoOf, plainSummary, oncePerAction, viewKinds, ENTITY_TYPES, ENTITY_WORD, ONE_WORD, inOrder, RANGES, DAY, startOfDay, rangeStart, runsOf, daysOf, hm, hms, actorName, actorLabel, severityOf, metaOf, isoDay, hasExplicitFilters, filtersFromUrl, OUTCOME_IDS, outcomeWord, isMine } from './activityData';
import { dateOf, dayOf, fmt, plural } from '../../nucleus/format';

// THE LOG — one ledger of every read, write and decision in this space. Across
// the top, how much and when (the all-time figures, who acted, the pulse of
// the whole range, counted on the server); the questions as chips; then the timeline, a table
// a day, one run per row. An entry opens in the inspector. Filters live in the address.

const MAX_SEEK_PAGES = 10;

export default function LogView() {
    const { groupId, spaceName, section, page: place } = usePlace();
    // a group's log is shared: its chips ask "By me", "People", "Apps" of everyone in it
    const VIEWS = viewsFor(!!groupId);
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const [openId, open, close] = useOpen();

    const init = useRef(null);
    if (init.current === null) init.current = filtersFromUrl(params);
    const I = init.current;

    const [view, setView] = useState(I.view);
    const [range, setRange] = useState(I.range);
    const [search, setSearch] = useState(I.search);
    const [query, setQuery] = useState(I.search);
    const [via, setVia] = useState(I.via);
    const [outcome, setOutcome] = useState(I.outcome);
    const [categories, setCategories] = useState(I.categories);
    const [entities, setEntities] = useState(I.entities);
    const [defaults, setDefaults] = useState(null);
    const [from, setFrom] = useState(I.from);
    const [to, setTo] = useState(I.to);
    const [customOpen, setCustomOpen] = useState(false);
    const [feed, setFeed] = useState(null); // null loading · { entries, total, hasMore, origins, entityCounts, pulse }
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [realms, setRealms] = useState([]);
    const [expanded, setExpanded] = useState(() => new Set());
    const [page, setPage] = useState(1);
    const [loadedPage, setLoadedPage] = useState(0);
    const [tick, setTick] = useState(0);
    // The entry a link asked for (?audit= / ?open= on arrival): the pages are
    // read on until it turns up, a few, not the whole ledger.
    const [seek, setSeek] = useState(() => params.get('audit') || params.get('open'));

    // ?audit= is the old name for ?open= on this page: accept it, then speak the new one.
    const audit = params.get('audit');
    const lastAudit = useRef(null);
    useEffect(() => {
        if (!audit) return;
        const first = lastAudit.current === null;
        lastAudit.current = audit;
        setSeek(audit);
        if (!first && !hasExplicitFilters(params)) clearAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audit]);

    // Your default kinds (log preferences) — the default for a view that has no
    // kinds of its own. Unreadable, the defaults stand in.
    useEffect(() => {
        let live = true;
        api.getLogPreferences()
            .then((p) => {
                const on = (p?.enabled_categories || []).filter((k) => KINDS[k]);
                if (live && on.length) setDefaults(KIND_IDS.filter((k) => on.includes(k)));
            })
            .catch(() => {});
        return () => { live = false; };
    }, []);

    useEffect(() => {
        const t = setTimeout(() => { if (search !== query) { setQuery(search); setPage(1); } }, 350);
        return () => clearTimeout(t);
    }, [search, query]);

    const viewSpec = VIEWS.find((v) => v.id === view) || VIEWS[0];
    // your saved default kinds are your own log's; a group's leads with what its people did
    const kinds = categories ? inOrder(categories, KIND_IDS) : viewKinds(viewSpec, groupId ? GROUP_DEFAULT_KINDS : defaults);
    const kindSet = new Set(kinds);
    const whoList = entities ? inOrder(entities, ENTITY_TYPES) : (viewSpec.entities || null);
    const whoSet = new Set(whoList || ENTITY_TYPES);
    const viaKey = via ? [...via].sort().join(',') : '';
    const outcomeKey = outcome ? inOrder(outcome, OUTCOME_IDS).join(',') : '';

    // NOT GATED ON THE PREFERENCES READ: the feed starts with the default kinds, and
    // if the person's own differ the kinds change and it reads again. Waiting added
    // a whole round trip before anything showed.
    const feedRef = useRef(feed);
    feedRef.current = feed;
    // What the facets held describe. The server builds them from the space, the
    // kinds and the dates alone — not the page, the cursor, who, via or the search
    // — so a read that changes only those keeps them instead of asking again.
    const facetsOf = useRef(null);
    useEffect(() => {
        let live = true;
        setLoading(true);
        // The first page reads the newest entries and the facets; each Load more
        // (and each step of a seek) continues from the previous page's cursor.
        const more = page > 1 && feedRef.current?.nextBefore;
        const facetKey = [groupId, kinds.join(','), range, from ? String(from) : '', to ? String(to) : '', tick].join('|');
        const held = !!feedRef.current && facetsOf.current === facetKey;
        const q = { per_page: '100', categories: kinds.join(',') };
        if (more) q.before = feedRef.current.nextBefore;
        if (more || held) q.include_facets = 'false';
        // the pulse, counted over the range on the server, read with the facets
        const sl = range !== 'all' || from || to ? slicesOf(from || to ? 'month' : range) : null;
        if (sl && !(more || held)) Object.assign(q, { pulse_start: new Date(sl.start).toISOString(), pulse_step: String(sl.step / 1000), pulse_count: String(sl.count) });
        if (whoList && whoList.length < ENTITY_TYPES.length) q.entity_types = whoList.join(',');
        if (viaKey) q.via = viaKey;
        if (outcomeKey) q.outcome = outcomeKey;
        if (query) q.search = query;
        const s = from || rangeStart(range);
        if (s) q.start_date = new Date(s).toISOString();
        if (to) q.end_date = new Date(to).toISOString();
        api.getActivityFeed(q, groupId)
            .then((j) => {
                if (!live) return;
                const got = j?.entries || [];
                const keep = more || held;
                if (!keep) facetsOf.current = facetKey;
                setFeed((cur) => ({
                    entries: page === 1 || !cur ? got : [...cur.entries, ...got],
                    // exact or null: the server no longer reports a capped count
                    total: typeof j?.total === 'number' ? j.total : null,
                    hasMore: Boolean(j?.has_more ?? j?.has_next),
                    nextBefore: j?.next_before || null,
                    origins: keep ? (cur?.origins || []) : (j?.origins || []),
                    entityCounts: keep ? (cur?.entityCounts || {}) : (j?.entity_counts || {}),
                    pulse: keep ? (cur?.pulse || null) : (Array.isArray(j?.pulse) ? j.pulse : null),
                }));
                setLoadedPage(page);
                setError(null);
            })
            .catch((e) => { if (live) setError(e?.message || 'The log could not be read.'); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, kinds.join(','), (whoList || []).join(','), viaKey, outcomeKey, query, range, from, to, groupId, tick]);

    useEffect(() => {
        let live = true;
        (groupId ? api.getGroupPrivacyRealms(groupId) : api.getPrivacyRealms())
            .then((r) => { if (live) setRealms(Array.isArray(r) ? r : []); })
            .catch(() => { if (live) setRealms([]); }); // a realm without an id still opens the Realms page
        return () => { live = false; };
    }, [groupId, tick]);

    // A new space starts a fresh read — only on a CHANGE, so an arriving link keeps its entry.
    const lastGroup = useRef(groupId);
    useEffect(() => {
        if (lastGroup.current === groupId) return;
        lastGroup.current = groupId;
        setFeed(null); setPage(1); setVia(null); setSeek(null);
    }, [groupId]);

    // The address mirrors the filters (replace, so Back is not a filter history).
    // ?audit= becomes ?open= here, in the same write, so the two never race.
    useEffect(() => {
        const next = new URLSearchParams(params);
        const put = (k, v) => (v ? next.set(k, v) : next.delete(k));
        put('view', view === 'all' ? '' : view);
        put('cat', categories ? inOrder(categories, KIND_IDS).join(',') : '');
        put('entity', entities ? inOrder(entities, ENTITY_TYPES).join(',') : '');
        put('via', viaKey);
        put('outcome', outcomeKey);
        put('range', range === 'week' ? '' : range);
        put('from', from ? isoDay(from) : '');
        put('to', to ? isoDay(to) : '');
        put('q', query);
        if (next.get('audit')) { next.set('open', next.get('audit')); next.delete('audit'); }
        if (next.toString() !== params.toString()) setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, categories, entities, viaKey, outcomeKey, range, from, to, query, audit]);

    // one entry per action: a node change is logged twice (audit and its history)
    const meId = user?.id;
    const mine = !!viewSpec.mine;
    const others = !!viewSpec.others;
    const entries = useMemo(() => {
        const once = oncePerAction(feed?.entries || []);
        // Me / By me: the people rows that are the viewer's own (the feed has no "who"
        // filter); on your own log a row naming no one is yours. People: the others.
        const own = (e) => isMine(e, meId, !!groupId);
        return mine ? once.filter(own) : others ? once.filter((e) => !own(e)) : once;
    }, [feed, mine, others, meId, groupId]);
    const origins = feed?.origins || [];
    const runs = useMemo(() => runsOf(entries), [entries]);
    const days = useMemo(() => daysOf(runs), [runs]);
    const current = openId ? entries.find((e) => String(e.id) === String(openId)) : null;
    const runOf = (id) => runs.find((r) => r.members.some((m) => String(m.id) === String(id)));

    // …lands ON the sought entry: its run opened and scrolled to, reading on if need be.
    const body = useRef(null);
    const found = !!seek && entries.some((e) => String(e.id) === String(seek));
    useEffect(() => {
        if (!seek || loading || found || error || loadedPage !== page) return;
        if (feed?.hasMore && page < MAX_SEEK_PAGES) setPage((p) => p + 1);
    }, [seek, loading, found, feed, page, loadedPage, error]);
    useEffect(() => {
        if (!found) return;
        const r = runOf(seek);
        if (r && r.members.length > 1) setExpanded((s) => (s.has(r.id) ? s : new Set(s).add(r.id)));
        requestAnimationFrame(() => {
            const el = body.current?.querySelector(`[data-entry="${(window.CSS?.escape || String)(String(seek))}"]`);
            el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        });
        setSeek(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [found]);
    // Is anything narrowing the log, beyond the whole of it?
    const narrowed = view !== 'all' || range !== 'all' || !!from || !!to || !!query || !!entities || !!via || !!outcome || kinds.length < KIND_IDS.length;
    const seekMissing = !!seek && !loading && !error && loadedPage === page && !found && !(feed?.hasMore && page < MAX_SEEK_PAGES);

    const reset = () => { setPage(1); };
    const clearAll = () => {
        setView('all'); setCategories(new Set(KIND_IDS)); setEntities(null); setVia(null); setOutcome(null);
        setRange('all'); setFrom(null); setTo(null); setSearch(''); setQuery(''); setPage(1);
    };
    const pickView = (id) => { setView(id); setCategories(null); setEntities(null); setVia(null); reset(); };
    const pickRange = (id) => { setRange(id); setFrom(null); setTo(null); reset(); };
    const toggleKind = (k) => {
        const c = new Set(kindSet);
        if (c.has(k)) c.delete(k); else c.add(k);
        setCategories(c.size ? c : null); reset();
    };
    const everyone = () => { setEntities(null); if (viewSpec.entities) setView('all'); reset(); };
    const toggleEntity = (t) => {
        const c = new Set(whoSet);
        if (c.has(t)) c.delete(t); else c.add(t);
        if (c.size === ENTITY_TYPES.length) everyone();
        else { setEntities(c.size ? c : null); reset(); }
    };
    // An app takes its delegates with it: "through Personas" means everything Personas ran.
    const toggleVia = (o, withFamily = true) => {
        const fam = [o.id, ...(withFamily ? (o.children || []).map((c) => c.id) : [])];
        const n = new Set(via || []);
        const on = n.has(o.id);
        fam.forEach((f) => (on ? n.delete(f) : n.add(f)));
        setVia(n.size ? n : null); reset();
    };
    const dropVia = (id) => {
        const root = origins.find((o) => o.id === id);
        if (root) { toggleVia(root); return; }
        const n = new Set(via || []); n.delete(id); setVia(n.size ? n : null); reset();
    };

    // A setting row of the kit's menu draws no count of its own (only a pick does), so
    // a checked row carries its figure in the label, in the menu's own count face.
    const withCount = (word, n) => (n == null ? word : <span className="activity-menu-label">{word}<span className="ofself-menu-count">{n}</span></span>);
    // WHO: one menu, the kinds of who acted and then each app that acted in this
    // range (an app's own agents beneath it)
    const whoItems = [
        { label: 'Everyone', selected: whoList == null && via == null, onSelect: () => { everyone(); setVia(null); reset(); } },
        { separator: true },
        ...ENTITY_TYPES.map((t) => ({ label: withCount(ENTITY_WORD[t], feed?.entityCounts?.[t] != null ? fmt(feed.entityCounts[t]) : null), checked: whoSet.has(t), onSelect: () => toggleEntity(t) })),
        ...(origins.length ? [{ separator: true }] : []),
        ...origins.flatMap((o) => [
            { label: withCount(o.label || '—', fmt(o.count)), checked: !!via?.has(o.id), onSelect: () => toggleVia(o) },
            ...(o.children || []).map((c) => ({ label: withCount(`↳ ${c.label || '—'}`, fmt(c.count)), checked: !!via?.has(c.id), onSelect: () => toggleVia(c, false) })),
        ]),
    ];
    const whoWord = (entities?.size || 0) + (via?.size || 0);
    const year = new Date().getFullYear();
    const dateItems = [
        { label: 'Any time', selected: range === 'all' && !from && !to, onSelect: () => pickRange('all') },
        { label: 'Last 90 days', onSelect: () => { setFrom(startOfDay(new Date(Date.now() - 90 * DAY))); setTo(null); reset(); } },
        { label: 'This year', onSelect: () => { setFrom(new Date(year, 0, 1)); setTo(null); reset(); } },
        { label: 'Last year', onSelect: () => { setFrom(new Date(year - 1, 0, 1)); setTo(new Date(year - 1, 11, 31, 23, 59, 59)); reset(); } },
        { separator: true },
        { label: 'Between dates', onSelect: () => setCustomOpen(true) },
    ];

    // What is narrowing the log beyond the view and the range chips, each removable.
    const chips = [];
    if (categories && categories.size < KIND_IDS.length) inOrder(categories, KIND_IDS).forEach((k) => chips.push({ lead: 'What', label: KINDS[k].word, off: () => toggleKind(k) }));
    if (entities) inOrder(entities, ENTITY_TYPES).forEach((t) => chips.push({ lead: 'Who', label: ENTITY_WORD[t], off: () => toggleEntity(t) }));
    if (via) {
        [...via].forEach((id) => {
            const root = origins.find((o) => o.id === id);
            const parent = origins.find((o) => (o.children || []).some((c) => c.id === id));
            if (!root && parent && via.has(parent.id)) return; // folded under its app
            const child = parent?.children.find((c) => c.id === id);
            chips.push({ lead: child ? ONE_WORD[child.type] || 'Delegate' : 'Through', label: (root || child)?.label || id, off: () => dropVia(id) });
        });
    }
    if (outcome) chips.push({ lead: 'Outcome', label: outcomeWord(outcome), off: () => { setOutcome(null); reset(); } });
    if (from || to) {
        chips.push({ lead: 'When', label: `${from ? dateOf(from) : 'the start'} – ${to ? dateOf(to) : 'now'}`, off: () => { setFrom(null); setTo(null); reset(); } });
    }
    if (query) chips.push({ lead: 'Find', label: `“${query}”`, off: () => { setSearch(''); setQuery(''); reset(); } });

    const labelOf = (e) => actorLabel(e, meId, !!groupId);
    const realmIdOf = (e) => realms.find((r) => r.name === e.actor?.privacy_realm)?.id || null;
    const menuVerb = (icon, word, on) => <span><Verb icon={icon} active={on}>{word}</Verb></span>;
    const openRun = openId ? runOf(openId) : null;
    const columns = runColumns({
        labelOf, meId, groupName: groupId ? spaceName : null, openId, expanded, onOpen: (id) => open(id),
        onToggle: (r) => setExpanded((s) => { const n = new Set(s); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; }),
    });

    return (
        <NucleusPage
            section={section.label}
            title={place.label}
            count={feed ? (feed.total ?? undefined) : undefined}
            verbs={<>
                <FindField value={search} onChange={setSearch} placeholder="Find in the log" />
                <Menu trigger={menuVerb(<UsersRound />, whoWord ? `Who · ${whoWord}` : 'Who', whoWord > 0)} items={whoItems} />
                <Menu trigger={menuVerb(<Calendar />, from || to ? 'Between dates' : 'Dates', !!(from || to))} items={dateItems} />
            </>}
            inspector={current && (
                <EntryInspector key={current.id} entry={current} run={runOf(current.id)} label={labelOf(current)} meId={meId} realmId={realmIdOf(current)} groupName={groupId ? spaceName : null} onClose={close} />
            )}
        >
            {/* whose log: yours, or the group's by name (L4) */}
            <SetHead
                title={groupId ? `${spaceName.charAt(0).toUpperCase()}${spaceName.slice(1)}’s log` : 'Your log'}
                count={feed ? feed.total : undefined} className="activity-log__head"
            />
            <div className="nx-filters activity-filters">
                <Segmented label="What to show" options={VIEWS.map((v) => ({ id: v.id, label: v.word }))} value={view} onChange={pickView} />
                <Segmented label="When" options={RANGES.map((r) => ({ id: r.id, label: r.word }))} value={from || to ? null : range} onChange={pickRange} />
            </div>

            <div className="activity-log" ref={body}>
                {feed?.pulse && (range !== 'all' || from || to) && <Pulse bins={feed.pulse} range={from || to ? 'month' : range} />}

                {chips.length > 0 && (
                    <div className="activity-narrow" aria-label="Filters">
                        {chips.map((c, i) => (
                            <span key={`${c.lead}-${c.label}-${i}`} className="activity-narrow__chip">
                                <b>{c.lead}</b> {c.label}
                                <button type="button" onClick={c.off} aria-label={`Remove ${c.label} filter`}><X size={11} strokeWidth={2.2} /></button>
                            </span>
                        ))}
                        <button type="button" className="nx-link activity-narrow__clear" onClick={clearAll}>Clear all</button>
                    </div>
                )}

                {seekMissing && (narrowed ? (
                    <div className="activity-notice" role="status">
                        <span>That entry isn’t in these filters or dates.</span>
                        <button type="button" className="nx-link" onClick={clearAll}>Show everything</button>
                    </div>
                ) : (
                    // Nothing is narrowing it, so "Show everything" would show
                    // the same page again: say what is actually true.
                    <div className="activity-notice" role="status">
                        <span>{feed?.hasMore
                            ? `That entry isn’t among the newest ${fmt(entries.length)} entries read here.`
                            : 'That entry isn’t in this log, or it isn’t one you can see.'}</span>
                    </div>
                ))}
                {error && (
                    <div className="activity-notice is-live" role="alert">
                        <span>{error}</span>
                        <button type="button" className="nx-link" onClick={() => setTick((n) => n + 1)}>Try again</button>
                    </div>
                )}
                {feed && !loading && !error && days.length === 0 && (
                    <div className="activity-notice">
                        <span>Nothing happened here in this range.</span>
                        {chips.length > 0 && <button type="button" className="nx-link" onClick={clearAll}>Clear all filters</button>}
                    </div>
                )}

                {days.map((d) => (
                    <section key={d.day} className="activity-day" aria-label={dayName(d.day)}>
                        <header className="activity-day__head">
                            <span>{dayName(d.day)}<i>{new Date(d.day).toLocaleDateString([], { day: 'numeric', month: 'long' })}</i></span>
                            <span>{fmt(d.runs.reduce((n, r) => n + r.members.length, 0))}</span>
                        </header>
                        <DataTable
                            id="activity-log" label={dayName(d.day)} columns={columns} rows={d.runs} keyOf={(r) => r.id}
                            sort={{ id: 'when', dir: 'desc' }} currentKey={openRun?.id} onOpen={(_, r) => open(r.lead.id)}
                        />
                    </section>
                ))}

                {feed?.hasMore && (
                    <button type="button" className="activity-more" onClick={() => setPage((p) => p + 1)} disabled={loading}>
                        {loading ? 'Reading…' : `Load more · ${fmt(entries.length)} shown`}
                    </button>
                )}
            </div>
            {customOpen && <CustomRange onClose={() => setCustomOpen(false)} onShow={(f, t) => { setFrom(f); setTo(t); setCustomOpen(false); reset(); }} />}
        </NucleusPage>
    );
}

function dayName(day) {
    const today = startOfDay(new Date()).getTime();
    if (day === today) return 'Today';
    if (day === today - DAY) return 'Yesterday';
    return new Date(day).toLocaleDateString([], { weekday: 'long' });
}

// ── the pulse: when things happened, one bar per stretch of time, one colour
function slicesOf(range, now = new Date()) {
    const today = startOfDay(now);
    if (range === 'today') return { word: 'today', start: today.getTime(), step: 3600_000, count: 24, label: (t) => new Date(t).toLocaleTimeString([], { hour: 'numeric' }) };
    if (range === 'week') return { word: '7 days', start: today.getTime() - 6 * DAY, step: 6 * 3600_000, count: 28, label: (t) => new Date(t).toLocaleDateString([], { weekday: 'short' }) };
    return { word: '30 days', start: today.getTime() - 29 * DAY, step: DAY, count: 30, label: dayOf };
}

const RANGE_TITLE = { today: 'Today', week: 'This week', month: 'Last 30 days' };

// `bins`: the server's count of entries in each slice (pulse_start/step/count)
export function Pulse({ bins, range }) {
    const sl = slicesOf(range);
    const every = Math.max(1, Math.floor(sl.count / 6));
    const when = (i) => (range === 'today' || range === 'week'
        ? new Date(sl.start + i * sl.step).toLocaleString([], range === 'today' ? { hour: 'numeric' } : { weekday: 'short', hour: 'numeric' })
        : dayOf(sl.start + i * sl.step));
    return (
        <Spark
            form="bars" className="activity-pulse" values={bins} title={RANGE_TITLE[range] || 'Last 30 days'}
            readout={(i) => (bins[i] > 0 ? `${when(i)} · ${plural(bins[i], 'thing')}` : null)}
            ticks={(i) => (i % every === 0 ? sl.label(sl.start + i * sl.step) : '')}
        />
    );
}

// ── one run: an actor doing one thing, once or many times in a row, as a
// table's row: who (their face), what (and, opened, each of the run), its kind
// by its dot, ×N to open the rest, the time ──
const stop = (ev) => { if (ev.key === 'Enter' || ev.key === ' ') ev.stopPropagation(); };

function runColumns({ labelOf, meId, groupName, openId, expanded, onOpen, onToggle }) {
    // in a group every entry names who did it: You, or the person
    const nameOf = (e) => (groupName ? whoOf(e, meId) : actorName(e));
    return [
        { id: 'who', label: 'Who', lock: true, min: 170, grow: 1.2, value: (r) => nameOf(r.lead),
          cell: (r) => {
              const e = r.lead;
              const label = labelOf(e);
              return (
                  <span className="activity-run__who" data-entry={e.id}>
                      <Face entry={e} size={30} />
                      <b>{nameOf(e)}</b>
                      {!(groupName && label === 'You') && <StatusPill tone="quiet">{label}</StatusPill>}
                  </span>
              );
          } },
        { id: 'what', label: 'What', min: 220, grow: 3, value: (r) => plainSummary(r.lead, meId, groupName),
          cell: (r) => {
              const e = r.lead;
              const sev = severityOf(e);
              const meta = metaOf(e);
              return (
                  <span className="activity-run__what">
                      <span className="activity-run__line">
                          <span className="activity-run__sum">{plainSummary(e, meId, groupName)}</span>
                          {e.success === false && <StatusPill status="refused">Refused</StatusPill>}
                          {sev && <StatusPill tone={sev === 'Warning' ? 'warn' : 'live'}>{sev}</StatusPill>}
                      </span>
                      {e.error_message && <span className="activity-run__error">{e.error_message}</span>}
                      {meta && <span className="activity-run__meta">{meta}</span>}
                      {r.members.length > 1 && expanded.has(r.id) && (
                          <span className="activity-run__members">
                              {r.members.slice(0, 40).map((m) => (
                                  <button key={m.id} type="button" data-entry={m.id}
                                      className={`activity-run__member${String(m.id) === String(openId) ? ' is-open' : ''}`}
                                      onClick={(ev) => { ev.stopPropagation(); onOpen(m.id); }} onKeyDown={stop}>
                                      <span className="activity-run__mtime">{hms(m.timestamp)}</span>
                                      <span className={m.resource?.endpoint ? 'is-mono' : ''}>{m.resource?.endpoint || m.resource?.title || m.resource?.label || m.summary}</span>
                                  </button>
                              ))}
                              {r.members.length > 40 && <span className="activity-run__more">+{r.members.length - 40} more in this run</span>}
                          </span>
                      )}
                  </span>
              );
          } },
        { id: 'kind', label: 'Kind', min: 120, grow: 1, value: (r) => kindOf(r.lead.category).word,
          cell: (r) => {
              const kind = kindOf(r.lead.category);
              return <span className="activity-run__kind"><span className="activity-run__dot" style={{ background: kind.color }} />{kind.word}</span>;
          } },
        { id: 'times', label: 'Times', num: true, min: 64, grow: 0, value: (r) => r.members.length,
          cell: (r) => {
              if (r.members.length < 2) return null;
              const open = expanded.has(r.id);
              return (
                  <button type="button" className={`activity-run__count${open ? ' is-open' : ''}`} aria-expanded={open}
                      onClick={(ev) => { ev.stopPropagation(); onToggle(r); }} onKeyDown={stop}
                      title={`${r.members.length} in a row, ${hm(r.members[r.members.length - 1].timestamp)}–${hm(r.lead.timestamp)}`}>
                      ×{r.members.length}<ChevronDown size={11} strokeWidth={2.2} aria-hidden="true" />
                  </button>
              );
          } },
        { id: 'when', label: 'When', min: 76, grow: 0, asc: false, className: 'activity-when', value: (r) => new Date(r.lead.timestamp).getTime(),
          cell: (r) => hm(r.lead.timestamp) },
    ];
}

function CustomRange({ onClose, onShow }) {
    const today = isoDay(new Date());
    const [f, setF] = useState(isoDay(new Date(Date.now() - 30 * DAY)));
    const [t, setT] = useState(today);
    return (
        <Modal open onClose={onClose} title="Activity between" size="sm">
            <form className="activity-custom" onSubmit={(e) => { e.preventDefault(); onShow(new Date(`${f}T00:00:00`), new Date(`${t}T23:59:59`)); }}>
                <label>From <input type="date" value={f} max={today} onChange={(e) => setF(e.target.value)} /></label>
                <label>To <input type="date" value={t} min={f} max={today} onChange={(e) => setT(e.target.value)} /></label>
                <SheetFoot>
                    <FootButton onClick={onClose}>Cancel</FootButton>
                    <FootButton primary type="submit">Show</FootButton>
                </SheetFoot>
            </form>
        </Modal>
    );
}
